import { getFriendlyConversationId } from '@provision/formatting-utils';
import { portalPaths } from '@provision/portal-routes';
import { EntityNames } from 'src/graphql/types';
import type { Notification } from './notification.types';

// notification.entity carries the DB entity name, which differs from the portal's
// own naming for sites (stored as Address) and is not consistently cased.
const NOTIFICATION_ENTITY_TO_ENTITY_NAME: Record<string, EntityNames> = {
  address: EntityNames.Site,
  client: EntityNames.Client,
  conversation: EntityNames.Conversation,
  platformuser: EntityNames.PlatformUser,
  project: EntityNames.Project,
  site: EntityNames.Site,
};

export function getNotificationEntityName(entity?: string | null): EntityNames | null {
  if (typeof entity !== 'string') {
    return null;
  }

  return NOTIFICATION_ENTITY_TO_ENTITY_NAME[entity.toLowerCase()] ?? null;
}

export function getNotificationConversationFriendlyId(
  notification: Pick<Notification, 'entityId' | 'parsedPayload'>,
): string | null {
  const conversationData = notification.parsedPayload?.conversation;
  if (!conversationData) {
    return null;
  }

  return (
    getFriendlyConversationId({
      workspaceConversationId: conversationData.workspaceId,
      conversationId: notification.entityId,
      partner: { partnerId: conversationData.partnerId },
    }) || null
  );
}

export function getNotificationTargetPath({
  entityName,
  entityId,
  conversationFriendlyId,
}: {
  entityName: EntityNames | null;
  entityId: string;
  conversationFriendlyId?: string | null;
}): string | null {
  switch (entityName) {
    case EntityNames.Conversation:
      return conversationFriendlyId
        ? portalPaths.conversation.full(conversationFriendlyId)
        : null;
    case EntityNames.Site:
      return portalPaths.site.full(entityId);
    case EntityNames.Client:
      return portalPaths.client.full(entityId);
    case EntityNames.Project:
      // Projects have no tab type and no full page — open the Projects grid
      // (the standard entity.all route the Projects nav item itself uses)
      // with the record peeked in the side panel.
      return portalPaths.entity.all({ entityName: 'projects', id: entityId });
    default:
      // platformUser (and any other unmapped entity) has no standard view to open
      return null;
  }
}
